import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AddTokenApi } from '../apis/add-token-api';
import type { TopupToken, TopupMethod } from '../models/topup.model';
import type { AddTokenFormValue } from '../models/add-token.model';

/**
 * AddTokenService — orchestrates "Ajouter un compte" form submission.
 *
 * Bridges the DynamicAddTokenFormComponent output + current TopupMethod context
 * into a well-formed API call, then maps the raw response back to a TopupToken
 * ready for insertion into the store's token list.
 *
 * Key responsibilities:
 *  1. Build `token_properties` from the dynamic form fields.
 *  2. Auto-inject fixed values from `processor.config` (e.g. swift_code for
 *     SUNU Bank, NSIA, Coris, Bank of Africa) — these are never shown in the UI.
 *  3. For mobile-money processors (phone param present): forward int_phone + phone
 *     as top-level fields in addition to token_properties.
 *  4. Accept the correct `tokenService` (1 = TOP_UP, 2 = WITHDRAWAL) from the caller
 *     rather than hardcoding it — both TopupStore and WithdrawalStore use this service.
 */
@Injectable({
  providedIn: 'root',
})
export class AddTokenService {
  private readonly addTokenApi = inject(AddTokenApi);

  /**
   * Submit a new token.
   *
   * @param form         - Dynamic form output: fields map + optional phone + optional name
   * @param method       - Currently selected TopupMethod (provides processor context)
   * @param tokenService - 1 = TOP_UP (Topup flow), 2 = WITHDRAWAL (Withdrawal flow)
   * @returns            - The created TopupToken, ready to insert into the store
   */
  createToken(
    form: AddTokenFormValue,
    method: TopupMethod,
    tokenService: number,
  ): Observable<TopupToken> {
    const processor = method.processor;
    if (!processor) {
      throw new Error('No processor available for the selected method.');
    }

    // ── 1. Start with dynamic form fields ──────────────────────────────────
    const tokenProperties: Record<string, string> = { ...form.fields };

    // ── 2. Auto-inject fixed values from processor.config ──────────────────
    // e.g. SUNU Bank config: { swift_code: { value: 'BPECTGTG', ... } }
    if (processor.config) {
      const config = processor.config as Record<string, { value?: string | number }>;
      for (const [key, entry] of Object.entries(config)) {
        if (entry?.value !== undefined) {
          tokenProperties[key] = String(entry.value);
        }
      }
    }

    // ── 3. Derive token display name ────────────────────────────────────────
    // Priority: explicit name field (mobile-money) > account_name param > processor display name
    const name =
      form.name?.trim() || form.fields['account_name']?.trim() || processor.processor_display_name;

    // ── 4. Call API ─────────────────────────────────────────────────────────
    return this.addTokenApi
      .createToken(
        name,
        processor._id,
        processor.payment_mode,
        tokenService,
        tokenProperties,
        form.phone?.intPhone,
        form.phone?.localNumber,
      )
      .pipe(
        map((res) => {
          if (!res.success || !res.token) {
            throw new Error(res.message ?? 'Impossible de créer le compte. Réessayez.');
          }
          // Map CreateTokenResponse.token → TopupToken shape
          const t = res.token;
          return {
            _id: t._id,
            display_key: t.display_key,
            token_name: t.token_name,
            token_type: t.token_type,
            token_service: t.token_service,
            processor_id: t.processor_id,
            status: t.status,
            logo_url: t.logo_url,
            token_properties: t.token_properties,
          } satisfies TopupToken;
        }),
      );
  }
}
